// English translations — primary source. Latvian mirrors this structure.
export const en = {
  meta: {
    title: "Toms Clarifies — Confusion is expensive. Clarity changes everything.",
  },
  nav: {
    home: "Home",
    forStudents: "For Students",
    forBusiness: "For Business",
    pricing: "Pricing",
    audit: "Free AI Audit",
    about: "About",
    contact: "Contact",
    cta: "Get in touch",
    menu: "Menu",
    close: "Close",
  },
  lang: {
    label: "Language",
    en: "English",
    lv: "Latviešu",
    short_en: "EN",
    short_lv: "LV",
  },
  common: {
    learnMore: "Learn more",
    readMore: "Read",
    requestLesson: "Request a lesson",
    bookAudit: "Book a free audit",
    seeAll: "See everything",
    next: "Next",
    back: "Back",
    submit: "Submit",
    sending: "Sending…",
    optional: "optional",
    required: "Required",
    seeBusiness: "Explore the business side",
    seeStudents: "Explore for students",
    seePricing: "See pricing",
    fromPrice: "from",
    perHour: "/ h",
    perPack: "/ 4-pack",
    contactMe: "Contact me",
  },
  home: {
    hero: {
      eyebrow: "Toms Clarifies — by Toms Reksna",
      title1: "Confusion is expensive.",
      title2: "Clarity changes everything.",
      subtitle: "I'm Toms Reksna. I help students truly understand physics and math, and I help adults and businesses turn AI into a practical advantage.",
      tagline: "From first principles in physics and mathematics to AI for learning and business.",
      ctaWatch: "Watch 90-second intro",
      ctaExplore: "Explore what I do",
    },
    paths: {
      eyebrow: "Two clear paths",
      title: "Pick the one that fits you.",
      description: "Same approach behind both: first principles, real understanding, practical action. Different audiences and outcomes.",
      students: {
        tag: "For students",
        title: "Tutoring in physics, math & programming",
        desc: "1-to-1 lessons that build deep, intuitive understanding — not algorithm-memorising. From high-school basics to university calculus and olympiad-level problems. Now also online and in English.",
        bullets: [
          "Socratic method — you think, I guide",
          "Verified results (students going from 4→10, 3→9)",
          "Latvian and English, online and in-person",
        ],
        cta: "See tutoring",
      },
      business: {
        tag: "For business & adults",
        title: "AI training, consulting & custom systems",
        desc: "Workshops, consulting, and built-for-you AI workflows for individuals, teams, and educators. Data, automation, storytelling, business development — with AI as a thinking partner.",
        bullets: [
          "AI workshops & corporate training",
          "Data analytics & storytelling with data",
          "Custom workflows and automation",
        ],
        cta: "See business services",
      },
    },
    intro: {
      eyebrow: "Short version",
      paragraph1: "I study, teach, and build around one question: how can people understand difficult things faster — and use that understanding better?",
      paragraph2: "Sometimes that means physics tutoring. Sometimes it means AI training for businesses and teachers. Sometimes it means building small tools that test better ways to learn, think, and work.",
      videoComing: "90-second introduction — coming soon",
      ctaAbout: "More about me",
      ctaAudit: "Take the free audit",
    },
    spotlight: {
      eyebrow: "Current spotlight",
      title: "What I'm sharing right now",
      description: "A small, rotating selection of articles, workshops, and experiments worth your time.",
      cards: [
        {
          tag: "Featured article",
          title: "How AI changes the way we should learn",
          desc: "A practical look at using AI as a thinking partner — without outsourcing your understanding.",
          cta: "Read",
        },
        {
          tag: "Upcoming workshop",
          title: "Practical AI for learning and business",
          desc: "A focused session on turning vague AI ideas into clear workflows and learning systems.",
          cta: "Join",
        },
        {
          tag: "Experiment",
          title: "AI-assisted learning vs traditional courses",
          desc: "A live experiment: can AI replace a pre-recorded course as a learning tool? Documented openly.",
          cta: "Follow along",
        },
      ],
    },
    closing: {
      eyebrow: "Not sure where to start?",
      title: "If you're a business, the free audit is the easiest first step.",
      description: "Five short questions, then leave your details. I'll get back to you with a focused next step within a few business days.",
      cta: "Take the free audit",
      ctaStudent: "Or — I'm a student",
    },
  },
  forStudents: {
    hero: {
      eyebrow: "For Students — Tutoring",
      title: "Physics, math, and more — actually understood.",
      description: "1-to-1 lessons that build deep, intuitive understanding. From high-school physics and math to university calculus, olympiad preparation, and programming. Now online and available in English as well as Latvian.",
    },
    subjects: {
      eyebrow: "Subjects I teach",
      title: "Pick what you need — I cover the spectrum.",
      standard: {
        label: "Standard",
        title: "High-school physics & math",
        desc: "Building solid fundamentals, exam preparation, recovering grades, and turning around 'I'm not a math person' beliefs.",
        items: ["Physics (kinematics → electromagnetism)", "Algebra & geometry", "Calculus introduction", "Statistics basics", "Exam preparation"],
      },
      advanced: {
        label: "Advanced",
        title: "University & olympiad-level",
        desc: "For ambitious students aiming higher — university entrance, olympiads, technical degree support.",
        items: ["University calculus & linear algebra", "Differential equations", "Olympiad physics & math", "Standardised exam prep (SAT, A-Level)", "University entrance preparation"],
      },
      programming: {
        label: "Programming & science",
        title: "Python, data, science projects",
        desc: "For students who want technical depth — programming, data analysis, science project mentorship.",
        items: ["Python programming", "Microsoft Excel (advanced)", "Data analysis fundamentals", "Science fair / project consultations", "Programming for problem-solving"],
      },
      extras: {
        label: "Extras",
        title: "Beyond lessons",
        desc: "Sometimes lessons aren't enough — or aren't what you need.",
        items: ["Homework & assignment review", "Exam answer revision", "Custom problem sets with theory", "Past-paper packs (on request)"],
      },
    },
    method: {
      eyebrow: "How I teach",
      title: "Socratic questioning. First principles. Real understanding.",
      paragraph1: "Years ago I noticed schools were teaching STEM more and more algorithmically: if you see X, do Y. My approach is the opposite. I use questions to engage the student, force them to think, and arrive at the answer themselves.",
      paragraph2: "The result is deep intuitive understanding — knowing not just what to do, but really understanding on a first-principles level why it is so. That's the foundation that makes hard problems feel possible.",
      principles: [
        { title: "First principles, not formulas", desc: "We start from why before we touch how." },
        { title: "Questions over answers", desc: "I guide. You think. The answer is yours." },
        { title: "Intuition is the goal", desc: "Real understanding feels obvious once you reach it." },
        { title: "AI as a tool, not a crutch", desc: "I teach how to use AI to learn faster — without outsourcing your thinking." },
      ],
    },
    testimonials: {
      eyebrow: "What students say",
      title: "Real results from real students.",
      items: [
        { quote: "I went from a 4 to a 10 in physics after a semester with Toms. He didn't just teach me the formulas — he made the subject finally make sense.", name: "K. B.", role: "High-school senior" },
        { quote: "From 3 to 9 in math. I never thought that was possible. The Socratic style felt slow at first but then everything just clicked.", name: "M. L.", role: "11th grade student" },
        { quote: "Toms helped me prepare for university entrance exams. Calm, clear, and never made me feel stupid for not knowing things.", name: "A. P.", role: "Engineering student" },
        { quote: "Honestly the first physics teacher who made me understand WHY things work, not just memorise.", name: "L. K.", role: "Physics student" },
        { quote: "He helped me through olympiad preparation. The depth of his explanations is on another level.", name: "R. S.", role: "Olympiad participant" },
        { quote: "Patient, structured, and somehow makes hard things feel manageable. Recommended without reservation.", name: "I. O.", role: "University student" },
      ],
    },
    pricing_summary: {
      eyebrow: "Pricing",
      title: "Transparent, tiered pricing.",
      description: "Prices depend on level (high-school vs university/olympiad) and language (Latvian or English). Bundles of 4 weekly lessons are cheaper per hour.",
      cta: "See full pricing",
    },
    ai_in_learning: {
      eyebrow: "AI in learning",
      title: "I teach students to use AI without losing their thinking.",
      paragraph: "The students who'll do best in the AI era aren't the ones who avoid AI or the ones who let it do everything. They're the ones who use it like a sharper study partner. I include that as part of my work whenever it's useful.",
    },
    cta: {
      title: "Ready to start?",
      description: "Send a short message: what subject, what level, when you'd like to start. I'll reply within a few business days.",
      button: "Request a lesson",
    },
  },
  forBusiness: {
    hero: {
      eyebrow: "For Business & Adults",
      title: "AI as a practical advantage — not theatre.",
      description: "Workshops, consulting, and built-for-you systems for individuals, teams, and educators. I help you cut through the noise and turn AI into something your business actually uses.",
    },
    thesis: {
      eyebrow: "My take",
      title: "Most AI strategies fail by being either too vague or too tool-shaped.",
      paragraph: "I don't sell another shiny tool. I help you understand what AI can actually do for your work, build the team literacy to use it well, and ship the specific workflows or systems that move your numbers. Then I make sure your people can keep going without me.",
    },
    tracks: {
      eyebrow: "How I can help",
      title: "Six tracks. Mixed and matched per engagement.",
      items: [
        {
          title: "AI Workshops & Corporate Training",
          desc: "From AI fundamentals and prompt engineering to advanced uses such as decision-making and problem-solving with AI. Half-day to multi-day formats, tailored to your team's actual work.",
          tag: "Workshop",
        },
        {
          title: "AI Consulting & 1-to-1 Coaching",
          desc: "Strategy without slideware. We clarify the real problem, identify where AI fits, and define concrete next steps. Coaching available for executives and specialists.",
          tag: "Consulting",
        },
        {
          title: "Data Analytics & Excel + Copilot",
          desc: "Years of specialty here. From basic Excel and PowerPoint skills to advanced Copilot, ChatGPT-assisted analysis, and helping people understand what data actually means.",
          tag: "Data",
        },
        {
          title: "Storytelling with Data",
          desc: "Reports are easy. Communicating findings clearly is hard. Building narratives, presentations, and the public-speaking around them — with AI helping on both visuals and structure.",
          tag: "Story",
        },
        {
          title: "Custom Solutions, Automation & Integration",
          desc: "Building lightweight AI workflows, automations, and tools that fit your existing systems. Including GDPR, privacy, and security considerations.",
          tag: "Build",
        },
        {
          title: "Business Development & LinkedIn",
          desc: "Lead generation, prospect research, outreach prep, exhibition prep, sales process optimisation — and LinkedIn profile + Sales Navigator training to back it up.",
          tag: "BizDev",
        },
      ],
    },
    text_as_data: {
      eyebrow: "Bonus — text is data too",
      title: "Numbers aren't the only data.",
      paragraph: "Your LinkedIn posts, your outreach campaigns, customer feedback, online reviews, public discussions — all data. AI lets us collect, process, and analyse text at scale to find what's actually working in your content and conversations.",
    },
    approach: {
      eyebrow: "Approach",
      title: "Three options per engagement.",
      items: [
        { num: "01", title: "Teach you to do it", desc: "You and your team build the skill. I run training, coach, and step away when you're ready." },
        { num: "02", title: "Build it for you", desc: "Custom workflows, tools, or learning systems delivered ready to use." },
        { num: "03", title: "Hybrid — train + build", desc: "I build the first version with you, then train your team to extend and maintain it." },
      ],
    },
    audit_cta: {
      eyebrow: "Start here",
      title: "A free, focused 30-minute AI audit.",
      description: "Five short questions and your contact details. I review your situation and reply with a focused next step. No sales-call sequence.",
      cta: "Take the free audit",
    },
    case: {
      eyebrow: "Recent work",
      title: "FITA — Software Engineering course",
      description: "Mentored a course on software engineering & maintenance covering Agile, OOP, QA, software architecture, design patterns, API integration, microservices fundamentals, debugging, and legacy code — with an AI angle threaded through the practical work.",
    },
  },
  pricing: {
    hero: {
      eyebrow: "Pricing",
      title: "Honest, tiered, no surprises.",
      description: "Tutoring is priced by level and language. Business engagements are scoped per project after a free audit.",
    },
    tutoring: {
      eyebrow: "Tutoring",
      title: "Lessons in physics, math, programming.",
      note: "All lessons online via video call. 4-lesson bundles are scheduled weekly at the same time. No refund if a lesson is missed (one reschedule allowed per bundle).",
      tiers: [
        {
          name: "Standard — Latvian",
          desc: "High-school physics and math. Basic programming. Exam preparation.",
          single: "€50",
          pack: "€180",
          packPerHour: "€45/h",
          highlight: false,
          features: ["High-school physics & math", "Algebra, calculus intro, statistics", "Latvian language", "1 reschedule per 4-pack"],
        },
        {
          name: "Advanced — Latvian",
          desc: "University-level math/physics. Olympiad preparation. Advanced programming.",
          single: "€65",
          pack: "€240",
          packPerHour: "€60/h",
          highlight: false,
          features: ["University calculus & linear algebra", "Olympiad preparation", "Advanced programming", "Latvian language"],
        },
        {
          name: "Standard — English",
          desc: "Same as standard, in English. For international students or English-language schools.",
          single: "€70",
          pack: "€260",
          packPerHour: "€65/h",
          highlight: true,
          features: ["High-school physics & math", "SAT / A-Level / IB prep", "English language", "1 reschedule per 4-pack"],
        },
        {
          name: "Advanced — English",
          desc: "University-level math/physics and olympiads, taught in English.",
          single: "€85",
          pack: "€320",
          packPerHour: "€80/h",
          highlight: false,
          features: ["University-level subjects", "Olympiad preparation", "Standardised tests (advanced)", "English language"],
        },
      ],
      extras: {
        title: "Extras",
        items: [
          { name: "Homework / assignment review", price: "from €15 / task" },
          { name: "Past-paper problem packs (theory + problems)", price: "from €120 / topic" },
          { name: "Science fair project consultation", price: "from €80 / session" },
        ],
      },
    },
    business: {
      eyebrow: "Business",
      title: "Workshops, consulting, custom builds.",
      description: "Every engagement starts with a free audit so the work fits your real problem. Indicative ranges below — final pricing is scoped per project.",
      tiers: [
        { name: "Free AI Audit", price: "€0", desc: "30-minute focused conversation after a short survey. No sales sequence.", cta: "Book audit", to: "/audit" },
        { name: "AI Workshop — half day", price: "from €800", desc: "3–4 hour session for teams. Tailored content, hands-on exercises.", cta: "Inquire", to: "/contact" },
        { name: "AI Workshop — full day", price: "from €1500", desc: "Full-day deep-dive workshop. Includes pre-call to tailor.", cta: "Inquire", to: "/contact" },
        { name: "Consulting / coaching session", price: "from €250", desc: "Per 90-minute working session. Bundled packs available.", cta: "Inquire", to: "/contact" },
        { name: "Custom solution / automation", price: "project quote", desc: "Built-for-you workflows, tools, or learning systems.", cta: "Inquire", to: "/contact" },
        { name: "Speaking & guest sessions", price: "project quote", desc: "Talks for events, schools, companies, communities.", cta: "Inquire", to: "/contact" },
      ],
    },
    note: {
      title: "About these prices",
      text: "Tutoring prices reflect online delivery and my methodology — small group sizes, careful preparation, and deep work per student. Business prices reflect actual scope and outcome rather than billed hours.",
    },
  },
  audit: {
    hero: {
      eyebrow: "Free AI Audit",
      title: "Five short questions. One focused next step.",
      description: "Tell me about your situation and what you'd like AI to do for your work. I'll review and reply with a focused recommendation within a few business days.",
    },
    progress: {
      step: "Step",
      of: "of",
    },
    steps: [
      {
        q: "What best describes your situation?",
        options: [
          { id: "indie", label: "Independent professional / consultant" },
          { id: "small", label: "Small team (2–20 people)" },
          { id: "mid", label: "Mid-size company (20–200)" },
          { id: "large", label: "Larger organisation (200+)" },
          { id: "educator", label: "Educator / school" },
          { id: "other_role", label: "Something else" },
        ],
      },
      {
        q: "Where do you see the biggest opportunity for AI in your work?",
        options: [
          { id: "productivity", label: "Day-to-day productivity & writing" },
          { id: "data", label: "Data, analytics, reporting" },
          { id: "sales", label: "Sales, outreach, marketing" },
          { id: "learning", label: "Learning, training, education" },
          { id: "build", label: "Custom workflows / automation / tools" },
          { id: "unsure", label: "Honestly — not sure yet" },
        ],
      },
      {
        q: "Where are you currently with AI?",
        options: [
          { id: "explore", label: "Just exploring / curious" },
          { id: "some", label: "I use some tools (ChatGPT, Copilot)" },
          { id: "comfy", label: "Comfortable user, looking to go further" },
          { id: "embedded", label: "AI is already in our workflows" },
        ],
      },
      {
        q: "Timeline?",
        options: [
          { id: "ideas", label: "Just gathering ideas" },
          { id: "quarter", label: "This quarter" },
          { id: "6mo", label: "Within 6 months" },
          { id: "soon", label: "As soon as possible" },
        ],
      },
    ],
    contact: {
      title: "One last step — your details.",
      description: "I read every submission personally and reply when there's a real fit.",
      fields: {
        name: "Your name",
        email: "Email",
        company: "Company / organisation",
        context: "Anything else I should know? (one or two sentences is plenty)",
      },
      submit: "Send my audit request",
    },
    success: {
      title: "Thanks — your audit request is in.",
      description: "I'll review your answers and reach out to you shortly with a focused next step. Usually within 2–3 business days.",
      action: "Back to home",
    },
    errors: {
      pickOne: "Please pick an option to continue.",
      name: "Please add your name.",
      email: "Please add a valid email.",
      generic: "Something went wrong. Please try again.",
    },
  },
  about: {
    hero: {
      eyebrow: "About",
      title: "Educator, builder, consultant — working on clarity.",
      description: "Based in Latvia. Working with people everywhere — across AI, physics, math, data, learning, and business systems.",
    },
    story: {
      eyebrow: "My story",
      title: "People don't usually struggle because they're not smart enough.",
      paragraph1: "I'm Toms Reksna, an educator, consultant, and builder based in Latvia. I work across AI, physics, mathematics, data, learning, and business systems.",
      paragraph2: "My work starts from a simple belief: people don't usually struggle because they're not smart enough. They struggle because complexity is presented badly, tools are used poorly, or the path forward is unclear.",
      paragraph3: "Most of what I do — teaching, writing, consulting, building — is just different shapes of the same job: removing avoidable confusion so the real work can begin.",
    },
    experience: {
      eyebrow: "What I work on",
      title: "Two intertwined fields of practice.",
      areas: [
        {
          tag: "Education",
          title: "Tutoring & learning",
          desc: "Years of private tutoring in physics and mathematics — from high-school basics to university calculus and olympiad preparation. Built around Socratic method and first-principles thinking. Increasingly extended with AI-as-tutor methods.",
        },
        {
          tag: "Business & AI",
          title: "AI workshops, consulting, data, automation",
          desc: "AI training and consulting for businesses, professionals, and educators. Data analytics and storytelling with data have been a specialty for years. Recent work includes mentoring a software engineering course at FITA.",
        },
      ],
    },
    approach: {
      eyebrow: "My approach",
      title: "Five principles that quietly shape everything I make.",
      items: [
        "First principles before shortcuts",
        "Understanding before automation",
        "AI as a thinking partner, not a replacement for thinking",
        "Practical tools over abstract theory",
        "Clear explanations, real examples, useful outcomes",
      ],
    },
    clarity: {
      eyebrow: "Why clarity matters",
      title: "Confusion is expensive — quietly, daily, and at scale.",
      paragraph1: "Confusion is what keeps capable students convinced they're not 'math-minded.' It's what makes teams spin on AI strategies that never ship. It's what turns useful tools into noise.",
      paragraph2: "Clarity is the opposite force. A clear explanation, a sharp question, a well-named problem — these are the levers that turn effort into outcomes.",
    },
    book: {
      eyebrow: "Currently writing",
      title: "A book on this work.",
      description: "Partly a way to describe my work to myself, partly a foundation for discussions with AI, partly content reused on this site. Slow, still evolving.",
    },
    social: {
      eyebrow: "Find me",
      title: "Where I actually post.",
    },
  },
  contact: {
    hero: {
      eyebrow: "Contact",
      title: "Tell me what you're working on.",
      description: "Tutoring, workshops, consulting, lab feedback, or just an interesting question — I read every message and reply when there's a real fit.",
    },
    info: {
      directLine: "Direct line",
      responseTime: "Response time",
      responseText: "Most replies come within 2–3 business days. For workshops and consulting, please include a short note on what you're trying to achieve.",
      basedIn: "Based in",
      basedText: "Latvia (CET / EET). Working with people everywhere.",
    },
    fields: {
      name: "Name",
      email: "Email",
      interest: "What are you interested in?",
      interestPlaceholder: "Pick one",
      message: "Message",
      messagePlaceholder: "What are you trying to do, learn, or build? A few sentences is plenty.",
      submit: "Send message",
      privacy: "By submitting you agree to receive a reply at the email above.",
    },
    interestOptions: {
      private_lessons: "Private lessons (tutoring)",
      ai_workshop: "AI workshop",
      consulting: "Consulting",
      tool_feedback: "Tool / lab feedback",
      speaking_webinar: "Speaking / webinar",
      something_else: "Something else",
    },
    success: {
      title: "Message received.",
      description: "Thanks for writing. I'll read it carefully and reply when there's a real fit.",
      another: "Send another",
    },
    errors: {
      name: "Please add your name.",
      email: "Please add a valid email.",
      interest: "Please pick what you're interested in.",
      message: "Add a short message — even one sentence helps.",
      generic: "Something went wrong sending your message. Please try again.",
    },
    toast: {
      sent: "Message sent. I'll reply soon.",
      received: "Message received. I'll be in touch.",
    },
  },
  forStudentsForm: {
    title: "Request a lesson",
    description: "Tell me a bit about what you need. I'll reply within a few business days with availability and confirmation.",
    fields: {
      name: "Your name",
      email: "Email",
      subject: "Subject",
      level: "Level",
      pack: "Package",
      language: "Language",
      schedule: "Preferred days / times",
      message: "Anything else? (optional)",
      submit: "Send request",
    },
    levels: {
      standard: "Standard (high-school)",
      advanced: "Advanced (uni / olympiad)",
      programming: "Programming / Excel / Python",
      extras: "Just homework review",
    },
    packs: {
      single: "Single 1-hour lesson",
      four: "4-lesson weekly bundle",
      ongoing: "Ongoing — let's discuss",
    },
    languages: {
      lv: "Latvian",
      en: "English",
    },
    subjectsHint: "e.g. Physics, Calculus, Python, SAT prep",
    schedulePlaceholder: "e.g. Tuesday evenings, weekends",
    success: {
      title: "Request sent.",
      description: "I'll review and reply within 2–3 business days with available slots.",
      another: "Send another request",
    },
    toast: {
      sent: "Request sent. I'll reply soon.",
    },
  },
  footer: {
    tagline: "Confusion is expensive. Clarity changes everything. A personal platform by Toms Reksna on learning, AI, and systems thinking — from first principles to practical action.",
    explore: "Explore",
    site: "Site",
    findMe: "Find me",
    basedIn: "Based in Latvia. Working with people everywhere.",
    copyrightSuffix: "All rights reserved.",
    builtWith: "Built with intention. Not a SaaS template.",
  },
  notFound: {
    eyebrow: "404",
    title: "That page doesn't exist.",
    description: "The link may be broken, or the page may have moved. Try heading back home.",
    cta: "Back to home",
  },
};
